import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, EyeOff, Eye, ChevronLeft, ChevronRight, Search, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';
import { formatarPreco } from '../data/produtos';

const STATUS = {
  ativo: { rotulo: 'Ativo', classe: 'bg-green-100 text-green-700' },
  inativo: { rotulo: 'Inativo', classe: 'bg-slate-200 text-slate-500' },
  esgotado: { rotulo: 'Esgotado', classe: 'bg-red-100 text-mk-red' },
};

export default function ProdutosList() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [status, setStatus] = useState('');
  const [baixo, setBaixo] = useState(false);
  const [page, setPage] = useState(1);
  const [excluindo, setExcluindo] = useState(null);

  const params = new URLSearchParams({ q, categoria, status, page, limit: 10, estoqueBaixo: baixo });
  const { data, isLoading } = useQuery({
    queryKey: ['admin-produtos', q, categoria, status, baixo, page],
    queryFn: async () => {
      const r = await apiFetch(`/admin/produtos?${params}`);
      if (!r.ok) throw new Error('falha');
      return r.json();
    },
  });
  const { data: cats } = useQuery({
    queryKey: ['admin-categorias'],
    queryFn: async () => (await apiFetch('/admin/categorias')).json(),
  });

  const alternar = async (p) => {
    const novoStatus = p.status === 'inativo' ? 'ativo' : 'inativo';
    const r = await apiFetch(`/admin/produtos/${p.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...p,
        marca: p.marca || null,
        precoPromocional: p.precoPromocional ?? null,
        estoqueCerroAzul: p.estoqueCerroAzul ?? null,
        estoqueDoutorUlysses: p.estoqueDoutorUlysses ?? null,
        mostrarEstoque: Boolean(p.mostrarEstoque),
        destaque: Boolean(p.destaque),
        baseAtualizadoEm: p.atualizadoEm,
        status: novoStatus,
      }),
    });
    if (r.ok) {
      toast.success(novoStatus === 'ativo' ? 'Produto ativado no catálogo.' : 'Produto ocultado do catálogo.');
      qc.invalidateQueries({ queryKey: ['admin-produtos'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    } else {
      toast.error('Não foi possível alterar o status.');
    }
  };

  const excluir = async () => {
    const r = await apiFetch(`/admin/produtos/${excluindo.id}`, { method: 'DELETE' });
    if (r.ok) {
      toast.success(`"${excluindo.nome}" excluído definitivamente.`);
      setExcluindo(null);
      qc.invalidateQueries({ queryKey: ['admin-produtos'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  return (
    <div data-testid="admin-products-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Produtos</h1>
        <Link
          data-testid="admin-novo-produto-button"
          to="/admin/produtos/novo"
          className="inline-flex items-center gap-2 rounded-full bg-mk-red px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-mk-redbright"
        >
          <PlusCircle size={16} /> Cadastrar produto
        </Link>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por nome ou código..."
            className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-mk-blue"
          />
        </div>
        <select data-testid="admin-filter-category" value={categoria} onChange={(e) => { setCategoria(e.target.value); setPage(1); }} className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium outline-none">
          <option value="">Todas as categorias</option>
          {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select data-testid="admin-filter-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium outline-none">
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
          <option value="esgotado">Esgotado</option>
        </select>
        <button
          data-testid="admin-filter-low-stock"
          onClick={() => { setBaixo(!baixo); setPage(1); }}
          className={`rounded-full border px-4 py-2.5 text-sm font-bold transition-colors ${baixo ? 'border-amber-500 bg-amber-100 text-amber-700' : 'border-slate-300 bg-white text-slate-500'}`}
        >
          Estoque baixo
        </button>
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-slate-400">Carregando...</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-2xl bg-white shadow-sm">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Produto</th>
                  <th className="px-4 py-3">Categoria</th>
                  <th className="px-4 py-3">Preço</th>
                  <th className="px-4 py-3">Estoque</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Atualizado</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => {
                  const preco = p.precoPromocional ?? p.precoVenda;
                  const st = STATUS[p.status];
                  return (
                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.fotos?.[0]} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                          <div className="min-w-0">
                            <p className="truncate font-bold text-slate-800">{p.nome}</p>
                            {p.sku && <p className="text-xs text-slate-400">SKU: {p.sku}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{p.categoriaNome || p.categoria}</td>
                      <td className="px-4 py-3 font-bold text-mk-blue">{formatarPreco(preco)}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {(p.estoqueCerroAzul ?? null) === null && (p.estoqueDoutorUlysses ?? null) === null
                          ? '—'
                          : (p.estoqueCerroAzul || 0) + (p.estoqueDoutorUlysses || 0)}
                      </td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[11px] font-extrabold ${st.classe}`}>{st.rotulo}</span></td>
                      <td className="px-4 py-3 text-xs text-slate-400">{new Date(p.atualizadoEm).toLocaleDateString('pt-BR')}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Link data-testid={`admin-edit-${p.id}`} to={`/admin/produtos/${p.id}`} aria-label="Editar" className="rounded-lg p-2 text-mk-blue hover:bg-blue-50"><Pencil size={16} /></Link>
                          <button data-testid={`admin-toggle-${p.id}`} onClick={() => alternar(p)} aria-label={p.status === 'inativo' ? 'Ativar' : 'Desativar'} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                            {p.status === 'inativo' ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                          <button data-testid={`admin-delete-${p.id}`} onClick={() => setExcluindo(p)} aria-label="Excluir" className="rounded-lg p-2 text-mk-red hover:bg-red-50"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.items.length === 0 && <p className="px-4 py-10 text-center text-sm text-slate-400">Nenhum produto encontrado.</p>}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-slate-400">{data.total} produto(s)</p>
            <div className="flex items-center gap-2">
              <button data-testid="admin-page-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-slate-300 bg-white p-2 disabled:opacity-40"><ChevronLeft size={16} /></button>
              <span className="text-sm font-bold text-slate-600">{data.page} / {data.pages}</span>
              <button data-testid="admin-page-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full border border-slate-300 bg-white p-2 disabled:opacity-40"><ChevronRight size={16} /></button>
            </div>
          </div>
        </>
      )}

      {excluindo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-mk-bluedark/70 p-4 backdrop-blur-sm" onClick={() => setExcluindo(null)}>
          <div data-testid="admin-delete-modal" className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">Excluir produto?</h3>
            <p className="mt-2 text-sm text-slate-500">
              O produto <span className="font-bold text-slate-800">"{excluindo.nome}"</span> será removido definitivamente do catálogo, junto com suas fotos enviadas. Essa ação não pode ser desfeita.
            </p>
            <p className="mt-2 text-xs text-slate-400">Dica: se preferir preservar o histórico, use a ação de desativar em vez de excluir.</p>
            <div className="mt-5 flex gap-2">
              <button data-testid="admin-delete-cancel" onClick={() => setExcluindo(null)} className="flex-1 rounded-full border-2 border-slate-200 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button data-testid="admin-delete-confirm" onClick={excluir} className="flex-1 rounded-full bg-mk-red py-2.5 text-sm font-bold text-white hover:bg-mk-redbright">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
